/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';
import Pagination from 'antd/es/pagination';
import { useIntl } from 'react-intl';
import { Table } from 'antd';
import { TableProps } from 'antd/lib/table';
import { DbFileActionLog } from '~src/types/db';
import { useFilesActionLog } from '~src/hooks/api-hooks/useFilesActionLog';
import { LOG_ACTION } from './LogModal';
import { formatDate } from '~src/utils/formatDate';

interface FileLogProps {
  state: boolean;
  before: number;
}

const TAKE_LIMIT = 12;

export const FileLog: React.FC<FileLogProps> = ({ state, before }) => {
  const intl = useIntl();
  const [skipLimit, setSkipLimit] = useState(0);
  const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>();

  const filesActionLogQuery = useFilesActionLog(
    { before: before, take: TAKE_LIMIT, skip: skipLimit * TAKE_LIMIT },
    { enabled: state }
  );

  useEffect(() => {
    if (state) {
      filesActionLogQuery.refetch();
    }
  }, [state]);

  useEffect(() => {
    if (!totalNumberOfItems) {
      setTotalNumberOfItems(filesActionLogQuery.data?.totalCount);
    }
  }, [filesActionLogQuery.data]);

  const fileActionLogColumns: TableProps<DbFileActionLog>['columns'] = [
    {
      title: intl.formatMessage({ id: 'date-and-time' }),
      dataIndex: 'action_at',
      key: 'date',
      render: (action_at, record) => formatDate(action_at, 'long'),
      width: '20%',
    },
    {
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'registration_name',
      key: 'name',
      width: '25%',
    },
    {
      title: intl.formatMessage({ id: 'path' }),
      dataIndex: 'registration_path',
      key: 'path',
      width: '30%',
    },
    {
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action_type',
      key: 'action',
      render: (action) =>
        Object.keys(LOG_ACTION).filter((x) => x === action).length >= 1
          ? intl.formatMessage({ id: LOG_ACTION[action] })
          : action,
      width: '10%',
    },
    {
      title: intl.formatMessage({ id: 'by' }),
      dataIndex: 'create_by_name',
      key: 'by',
      width: '15%',
    },
  ];

  const filesActionLogSource = useMemo(
    () =>
      (filesActionLogQuery.data?.logs || []).map((element) => ({
        ...element,
        key: element.action_id,
      })),
    [filesActionLogQuery.data]
  );

  return (
    <div>
      <div style={{ position: 'relative', height: 'min(65vh, 600px)' }}>
        <Table<DbFileActionLog>
          columns={fileActionLogColumns}
          dataSource={filesActionLogSource}
          pagination={false}
          loading={filesActionLogQuery.isLoading}
          scroll={{ y: 'min(50vh, 450px)', scrollToFirstRowOnChange: true }}
        />
      </div>
      {totalNumberOfItems && totalNumberOfItems > 0 && (
        <Pagination
          style={{ position: 'absolute', bottom: 40, right: 0, margin: '20px' }}
          total={totalNumberOfItems}
          pageSize={12}
          showSizeChanger={false}
          onChange={(page) => {
            setSkipLimit(page - 1);
          }}
        />
      )}
    </div>
  );
};
